import { useTranslation } from 'react-i18next';
import {
  Card, CardContent, CardHeader, CircularProgress, Drawer,
} from '@mui/material';
import { Box } from '@mui/system';
import { useState } from 'react';
import { AxiosError } from 'axios';
import STRING_KEYS from '../../language/keys';
import { HISTORY_ACTION_TYPE } from '../../consts/enums';
import { sortByDate } from '../HistoryPanel/utils/sortByDate';
import { HistoryCounter } from '../HistoryPanel/HistoryCounter';
import { HistoryEarthBorn } from '../HistoryPanel/HistoryEarthBorn';
import { HistoryHeader } from '../HistoryPanel/HistoryHeader';
import { HistoryErrorMessage } from '../HistoryPanel/HistoryErrorMessage';
import { HistoryNoResultsMessage } from '../HistoryPanel/HistoryNoResultsMessage';
import { HTTP_STATUSES } from '../../consts/apiCodes';
import { NoPermissions } from '../NoPermissions';
import { HistoryListView } from './HistoryListView';
import { HistoryFilters } from './HistoryFilters';
import { getFullName } from './utils';
import { style } from './style';

type HistoryMainPanelProps = {
  onCloseClick: () => void,
  isOpened: boolean,
  data: HistoryLogEntryResponse[],
  isError: boolean,
  error: AxiosError | null,
  isFetching: boolean,
  entity: string,
}

export const HistoryMainPanel = ({
  onCloseClick,
  isOpened,
  isFetching,
  isError,
  error,
  data,
  entity,
}:HistoryMainPanelProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const [activityFilters, setActivityFilters] = useState<string[]>([]);
  const [madeByFilters, setMadeByFilters] = useState<string[]>([]);
  const [userQuery, setUserQuery] = useState<string>('');
  const handleActivityChanged  = (val:string[]) => {
    setActivityFilters([...val]);
  };
  const handleMadeByChanged = (val:string[]) => {
    setMadeByFilters([...val]);
  };

  const clearFilters = () => {
    handleActivityChanged([]);
    setMadeByFilters([]);
  };
  const filteredHistory = data.filter((historyData: HistoryLogEntryResponse) => {
    if (activityFilters.length === 0 && madeByFilters.length === 0) {
      return true;
    }

    if (activityFilters.includes(historyData.actionType)) {
      return true;
    }

    if (historyData?.data?.status && activityFilters.includes(HISTORY_ACTION_TYPE.STATUS_CHANGED)) {
      return true;
    }

    if (madeByFilters.includes(historyData?.user.id)) {
      return true;
    }
    if (madeByFilters.includes(getFullName(historyData.user))) {
      return true;
    }

    return false;
  });
  const sortHistory = sortByDate(filteredHistory);

  const handleUserQueryChanged = (query: string) => {
    setUserQuery(query);
  };

  const renderList = () => {
    return (
      sortHistory.length !== 0 ? (
        <>
          <HistoryListView
            data={ sortHistory }
            titleEditor={ entity }
          />
          <HistoryEarthBorn />
        </>
      ) : (
        <HistoryNoResultsMessage />
      )
    );
  };

  const renderContent = () => {
    if (isFetching) {
      return (
        <Box sx={ style.card.circular }>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <CardContent sx={ style.card.content.sx }>
        <HistoryCounter counter={ sortHistory.length } />
        <HistoryFilters
          onActivityChanged={ handleActivityChanged }
          clearFilters={ clearFilters }
          onMadeByChanged={ handleMadeByChanged }
          activityFilters={ activityFilters }
          madeByFilters={ madeByFilters }
          data={ data }
          onUserQueryTextChanged={ handleUserQueryChanged }
          userQueryText={ userQuery }
          entity={ entity }
        />
        {renderList()}
      </CardContent>
    );
  };

  const renderErrorMessage = () => {
    if (error?.response?.status === HTTP_STATUSES.FORBIDDEN) {
      return <NoPermissions />;
    }

    return <HistoryErrorMessage />;
  };

  return (
    <Drawer
      anchor="right"
      open={ isOpened }
      variant="temporary"
      sx={ style.card.drawer }
      onClose={ onCloseClick }
    >
      <Card
        sx={ style.card.sx }
      >
        <CardHeader
          sx={ style.card.header }
          title={ (
            <HistoryHeader
              entityName={ translate(STRING_KEYS.ENTITY_NAME, { entity })  }
              onCloseClick={ onCloseClick }
            />
          ) }
        />
        {isError && renderErrorMessage()}
        {!isError && renderContent()}
      </Card>
    </Drawer>
  );
};
